<template>
    <div>
        <div class="mb-8">
            <strong class="block text-slate-900 text-base leading-6 font-semibold mb-2"> Campaign Name </strong>
            <div v-if="!editing" class="flex gap-2">
                {{ campaignName }}
                <vx-button
                    @click="editing = true"
                    type="button"
                    size="xs"
                    color="muted"
                    data-test="launch-preview-edit-name"
                >
                    Edit
                </vx-button>
            </div>
            <div v-else class="flex gap-2 [&>div>input]:h-12">
                <vx-input
                    :maxlength="84"
                    v-model="campaignName"
                    size="lg"
                    @input="emit('update:name', campaignName)"
                ></vx-input>
                <vx-button
                    @click="saveName()"
                    type="button"
                    size="lg"
                    color="primary"
                    data-test="launch-preview-edit-name-save"
                >
                    Save
                    <font-awesome-icon widthAuto :icon="faCheck"></font-awesome-icon>
                </vx-button>
            </div>
        </div>
        <div class="mb-8">
            <strong class="block text-slate-900 text-base leading-6 font-semibold mb-2"> Audience Size </strong>
            <div class="flex items-center gap-2">
                <div v-if="props.groupIds?.length">
                    {{ pluralized('group', props.groupIds.length, true) }}
                </div>
                <font-awesome-icon widthAuto v-if="props.groupIds?.length" :icon="faArrowRight"></font-awesome-icon>
                <div>
                    <span v-if="estimation != null">
                        {{ estimationTitle }}
                    </span>
                    <vx-icon-loading v-else spin></vx-icon-loading>
                    <span @click.stop="estimationModal = true" class="text-xs font-normal cursor-pointer">
                        (est
                        <font-awesome-icon widthAuto :icon="faCircleInfo"></font-awesome-icon>
                        )
                    </span>
                </div>
            </div>
        </div>
        <div>
            <strong class="block text-slate-900 text-base leading-6 font-semibold mb-2"> Message Preview </strong>
            <div
                class="p-4 whitespace-pre-line bg-white shadow text-slate-700 text-sm font-normal rounded-[16px] rounded-bl"
            >
                {{ props.bodyMessage }}
            </div>
            <div
                v-if="props.mediaUrl"
                class="mt-2 p-4 bg-white shadow rounded-[16px] rounded-bl max-w-[208px] max-h-[500px]"
            >
                <img :src="props.mediaUrl" class="w-full block object-cover rounded rounded-b-sm" />
            </div>
        </div>
    </div>

    <vx-modal v-model:visible="estimationModal" class="max-w-lg">
        <template v-slot:header> Recipient Estimation </template>
        <p class="text-slate-500 text-base mb-2">
            The total number of recipients is based on several variables that can change all the way up to the time a
            message sends. As a result, we can only provide an exact number once messages go out.
        </p>
        <p class="text-slate-500 text-base">
            However, we do our best to provide an accurate estimation so you have a general idea of how many people
            you'll reach.
        </p>
    </vx-modal>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCheck, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { VxInput, VxButton, VxModal, VxIconLoading } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { formatCount, pluralized } from '../filters';

const emit = defineEmits(['save:name']);

const props = defineProps({
    mediaUrl: {
        type: String,
        required: false,
    },
    bodyMessage: {
        type: String,
        required: true,
    },
    estimation: {
        required: false,
    },
    groupIds: {
        type: Array,
    },
});

const campaignName = defineModel('name', {
    type: String,
    required: true,
});

const editing = ref(false);
const estimationModal = ref(false);

const saveName = () => {
    editing.value = false;
    emit('save:name');
};

const estimationTitle = computed(() => {
    return props.estimation !== null ? `${formatCount(props.estimation)} Recipients` : '-';
});
</script>
